/**
 * New Travel Risk Brief form model and payload mapping.
 *
 * Extracted from TripPlanner.tsx so the pure form helpers can be imported by
 * tests (and kept out of the component file, which must only export components
 * for React Fast Refresh).
 */

import type {
  Accommodation,
  CreateTripBriefInput,
  DiningReservation,
  FlightSegment,
  GroundTransportMode,
  ItineraryLeg,
  MeetingVenue,
  TripClassification,
  TripLogistics,
  TripPurpose,
} from '../services/trip-briefs.service';

export interface NewBriefFormState {
  protectedPersonId: string;
  title: string;
  purpose: TripPurpose;
  classification: TripClassification;
  requestingParty: string;
  executiveIndustry: string;
  departDate: string;
  returnDate: string;
  briefNeededByDate: string;
  itinerary: ItineraryLeg[];
  // Logistics
  departureAirport: string;
  arrivalAirport: string;
  flights: FlightSegment[];
  groundTransportMode: GroundTransportMode | '';
  groundTransportNotes: string;
  accommodations: Accommodation[];
  meetings: MeetingVenue[];
  dining: DiningReservation[];
  // Notes & special considerations
  travelerNotes: string;
  lgbtqTraveler: boolean;
  soloFemaleTraveler: boolean;
  publicFacingProfile: boolean;
  securityDetail: 'NONE' | 'COVERT' | 'OVERT';
  medicalConditions: string;
  dietaryRequirements: string;
  disabilityAccommodations: string;
  additionalNotes: string;
}

export function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyForm(): NewBriefFormState {
  return {
    protectedPersonId: '',
    title: '',
    purpose: 'BUSINESS',
    classification: 'CONFIDENTIAL',
    requestingParty: '',
    executiveIndustry: '',
    departDate: '',
    returnDate: '',
    briefNeededByDate: '',
    itinerary: [{ id: uid('leg'), city: '', country: '', startDate: '', endDate: '' }],
    departureAirport: '',
    arrivalAirport: '',
    flights: [],
    groundTransportMode: '',
    groundTransportNotes: '',
    accommodations: [],
    meetings: [],
    dining: [],
    travelerNotes: '',
    lgbtqTraveler: false,
    soloFemaleTraveler: false,
    publicFacingProfile: false,
    securityDetail: 'NONE',
    medicalConditions: '',
    dietaryRequirements: '',
    disabilityAccommodations: '',
    additionalNotes: '',
  };
}

export function isFormValid(form: NewBriefFormState): boolean {
  if (!form.protectedPersonId || !form.title.trim() || !form.departDate || !form.returnDate) {return false;}
  if (form.itinerary.length === 0) {return false;}
  return form.itinerary.every((leg) => leg.city.trim() && leg.country.trim() && leg.startDate && leg.endDate);
}

export function buildLogistics(form: NewBriefFormState): TripLogistics | undefined {
  const logistics: TripLogistics = {};
  if (form.departureAirport.trim()) {logistics.departureAirport = form.departureAirport.trim();}
  if (form.arrivalAirport.trim()) {logistics.arrivalAirport = form.arrivalAirport.trim();}
  if (form.flights.length > 0) {logistics.flights = form.flights;}
  if (form.groundTransportMode) {logistics.groundTransportMode = form.groundTransportMode;}
  if (form.groundTransportNotes.trim()) {logistics.groundTransportNotes = form.groundTransportNotes.trim();}
  if (form.accommodations.length > 0) {logistics.accommodations = form.accommodations;}
  if (form.meetings.length > 0) {logistics.meetings = form.meetings;}
  if (form.dining.length > 0) {logistics.dining = form.dining;}
  return Object.keys(logistics).length > 0 ? logistics : undefined;
}

export function formToCreateInput(form: NewBriefFormState): CreateTripBriefInput {
  return {
    protectedPersonId: form.protectedPersonId,
    title: form.title.trim(),
    purpose: form.purpose,
    classification: form.classification,
    requestingParty: form.requestingParty.trim() || undefined,
    executiveIndustry: form.executiveIndustry.trim() || undefined,
    departDate: new Date(form.departDate).toISOString(),
    returnDate: new Date(form.returnDate).toISOString(),
    briefNeededByDate: form.briefNeededByDate ? new Date(form.briefNeededByDate).toISOString() : undefined,
    itinerary: form.itinerary.map((leg) => ({
      ...leg,
      startDate: new Date(leg.startDate).toISOString(),
      endDate: new Date(leg.endDate).toISOString(),
    })),
    logistics: buildLogistics(form),
    travelerNotes: form.travelerNotes.trim() || undefined,
    specialConsiderations: {
      lgbtqTraveler: form.lgbtqTraveler || undefined,
      soloFemaleTraveler: form.soloFemaleTraveler || undefined,
      publicFacingProfile: form.publicFacingProfile || undefined,
      securityDetail: form.securityDetail,
      medicalConditions: form.medicalConditions.trim() || undefined,
      dietaryRequirements: form.dietaryRequirements.trim() || undefined,
      disabilityAccommodations: form.disabilityAccommodations.trim() || undefined,
      additionalNotes: form.additionalNotes.trim() || undefined,
    },
  };
}
